import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Upload, X, FileText, LogOut, Link as LinkIcon, ArrowRight } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import api from '../lib/api';
import { removeToken, getUser } from '../lib/auth';

export default function EnhancedApplyPage() {
  const { roleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(false);
  const [loadingRole, setLoadingRole] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [role, setRole] = useState(null);
  const eligibility = location.state?.eligibility;

  const [formData, setFormData] = useState({
    // Personal (auto-filled)
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    // Professional
    past_experience: '',
    current_ctc: '',
    expected_ctc: '',
    experience_years: '',
    notice_period: '',
    // Documents
    resume_type: 'upload',
    resume_file_id: null,
    resume_file_name: '',
    resume_url: '',
    portfolio_type: 'upload',
    portfolio_file_id: null,
    portfolio_file_name: '',
    portfolio_url: '',
    // Custom fields
    custom_fields: {},
  });

  useEffect(() => {
    if (!roleId) {
      toast.error('No role selected');
      navigate('/roles');
      return;
    }
    if (!eligibility) {
      toast.error('Please acknowledge eligibility first');
      navigate(`/roles/${roleId}`);
      return;
    }
    fetchRole();
  }, [roleId, eligibility, navigate]);

  const fetchRole = async () => {
    try {
      const response = await api.get(`/api/roles/${roleId}`);
      setRole(response.data);
      
      // Initialize custom fields
      const customFieldsInit = {};
      if (response.data.custom_fields) {
        response.data.custom_fields.forEach(field => {
          customFieldsInit[field.field_name] = '';
        });
      }
      setFormData(prev => ({ ...prev, custom_fields: customFieldsInit }));
    } catch (error) {
      toast.error('Failed to load role details');
      navigate('/roles');
    } finally {
      setLoadingRole(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    setUploadingFile(type);
    const fileFormData = new FormData();
    fileFormData.append('file', file);

    try {
      const response = await api.post('/api/files/upload', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (type === 'resume') {
        setFormData(prev => ({
          ...prev,
          resume_file_id: response.data._id,
          resume_file_name: response.data.filename,
        }));
        toast.success('Resume uploaded successfully!');
      } else if (type === 'portfolio') {
        setFormData(prev => ({
          ...prev,
          portfolio_file_id: response.data._id,
          portfolio_file_name: response.data.filename,
        }));
        toast.success('Portfolio uploaded successfully!');
      }
    } catch (error) {
      toast.error(`Failed to upload ${type}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const resumeDropzone = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    maxSize: 10485760,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        await handleFileUpload(acceptedFiles[0], 'resume');
      }
    },
  });

  const portfolioDropzone = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/zip': ['.zip'],
    },
    maxFiles: 1,
    maxSize: 50485760,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        await handleFileUpload(acceptedFiles[0], 'portfolio');
      }
    },
  });

  const validateForm = () => {
    if (!formData.past_experience || formData.past_experience.length < 50) {
      toast.error('Please provide at least 50 characters for past experience');
      return false;
    }
    if (!formData.expected_ctc || parseFloat(formData.expected_ctc) <= 0) {
      toast.error('Please provide expected CTC');
      return false;
    }
    if (!formData.experience_years || parseFloat(formData.experience_years) < 0) {
      toast.error('Please provide years of experience');
      return false;
    }
    // Resume validation
    if (formData.resume_type === 'upload' && !formData.resume_file_id) {
      toast.error('Please upload your resume');
      return false;
    }
    if (formData.resume_type === 'url' && !formData.resume_url) {
      toast.error('Please provide resume URL');
      return false;
    }
    // Custom fields validation
    if (role?.custom_fields) {
      for (const field of role.custom_fields) {
        if (field.required && !formData.custom_fields[field.field_name]) {
          toast.error(`Please fill required field: ${field.field_name}`);
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        role_id: roleId,
        professional: {
          past_experience: formData.past_experience,
          current_ctc: formData.current_ctc ? parseFloat(formData.current_ctc) : null,
          expected_ctc: parseFloat(formData.expected_ctc),
          experience_years: parseFloat(formData.experience_years),
          notice_period: formData.notice_period || null,
        },
        docs: {
          resume_type: formData.resume_type,
          resume_file_id: formData.resume_file_id,
          resume_url: formData.resume_url || null,
          portfolio_type: formData.portfolio_type,
          portfolio_file_id: formData.portfolio_file_id,
          portfolio_url: formData.portfolio_url || null,
        },
        eligibility_acknowledged: eligibility,
        custom_fields: formData.custom_fields,
      };

      await api.post('/api/applications', payload);
      toast.success('Application submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loadingRole) {
    return (
      <div className="min-h-screen bg-white/40 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white/40">
      {/* Header */}
      <header className="bg-white border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-navy-900">girgit</h1>
                <p className="text-xs text-neutral-600">Application</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/roles"
                className="text-neutral-700 hover:text-navy-900 font-medium transition-colors"
              >
                Browse Roles
              </Link>
              <Link
                to="/dashboard"
                className="text-neutral-700 hover:text-navy-900 font-medium transition-colors"
              >
                My Applications
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-neutral-700 hover:text-navy-900 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-12 px-4" data-testid="application-form">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-navy-900 mb-2">
            Apply for {role?.title}
          </h1>
          <p className="text-lg text-neutral-600">
            {role?.department} • {role?.location} • {role?.employment_type}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information (readonly) */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8">
            <h2 className="text-2xl font-semibold text-navy-800 mb-6">
              Personal Information
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  readOnly
                  className="w-full px-4 py-3 border border-white/40 rounded-xl bg-white/40 text-neutral-600"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    readOnly
                    className="w-full px-4 py-3 border border-white/40 rounded-xl bg-white/40 text-neutral-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    readOnly
                    className="w-full px-4 py-3 border border-white/40 rounded-xl bg-white/40 text-neutral-600"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Professional Experience */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8">
            <h2 className="text-2xl font-semibold text-navy-800 mb-6">
              Professional Experience
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Past Experience * (Describe your work history)
                </label>
                <textarea
                  required
                  value={formData.past_experience}
                  onChange={(e) => setFormData({ ...formData, past_experience: e.target.value })}
                  rows={8}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                  placeholder="Describe your work history, key projects, achievements, and relevant experience..."
                  data-testid="past-experience-textarea"
                />
                <p className="text-sm text-neutral-500 mt-1">
                  {formData.past_experience.length} characters (minimum 50)
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Current CTC (optional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-neutral-500">₹</span>
                    <input
                      type="number"
                      value={formData.current_ctc}
                      onChange={(e) => setFormData({ ...formData, current_ctc: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="1200000"
                      data-testid="current-ctc-input"
                    />
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">Annual salary in INR</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Expected CTC *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-neutral-500">₹</span>
                    <input
                      type="number"
                      required
                      value={formData.expected_ctc}
                      onChange={(e) => setFormData({ ...formData, expected_ctc: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="1500000"
                      data-testid="expected-ctc-input"
                    />
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">Annual salary in INR</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Total Experience *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.5"
                    min="0"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                    placeholder="5"
                    data-testid="experience-years-input"
                  />
                  <p className="text-xs text-neutral-500 mt-1">Years (e.g., 2.5)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Notice Period
                  </label>
                  <input
                    type="text"
                    value={formData.notice_period}
                    onChange={(e) => setFormData({ ...formData, notice_period: e.target.value })}
                    className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                    placeholder="30 days / Immediate"
                    data-testid="notice-period-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Resume */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8">
            <h2 className="text-2xl font-semibold text-navy-800 mb-6">Resume *</h2>
            
            {/* Toggle */}
            <div className="flex gap-4 mb-6">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, resume_type: 'upload', resume_url: '' })}
                className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                  formData.resume_type === 'upload'
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-white/40 bg-white text-neutral-700 hover:border-slate-400'
                }`}
                data-testid="resume-upload-toggle"
              >
                <Upload className="w-5 h-5 inline mr-2" />
                Upload File
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, resume_type: 'url', resume_file_id: null, resume_file_name: '' })}
                className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                  formData.resume_type === 'url'
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-white/40 bg-white text-neutral-700 hover:border-slate-400'
                }`}
                data-testid="resume-url-toggle"
              >
                <LinkIcon className="w-5 h-5 inline mr-2" />
                Provide URL
              </button>
            </div>

            {formData.resume_type === 'upload' ? (
              !formData.resume_file_id ? (
                <div
                  {...resumeDropzone.getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    resumeDropzone.isDragActive
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-white/40 hover:border-teal-500'
                  }`}
                  data-testid="resume-dropzone"
                >
                  <input {...resumeDropzone.getInputProps()} />
                  {uploadingFile === 'resume' ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-12 h-12 text-teal-500 animate-spin mb-4" />
                      <p className="text-neutral-600">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-12 h-12 text-neutral-400 mb-4" />
                      <p className="text-neutral-600 mb-2">
                        Drag & drop your resume here, or click to browse
                      </p>
                      <p className="text-sm text-neutral-500">PDF, DOC, DOCX (max 10MB)</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-emerald-600" />
                    <div>
                      <p className="font-medium text-navy-800">{formData.resume_file_name}</p>
                      <p className="text-sm text-emerald-600">Upload complete</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, resume_file_id: null, resume_file_name: '' })}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )
            ) : (
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Resume URL *
                </label>
                <input
                  type="url"
                  required={formData.resume_type === 'url'}
                  value={formData.resume_url}
                  onChange={(e) => setFormData({ ...formData, resume_url: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="Link to your resume"
                  data-testid="resume-url-input"
                />
              </div>
            )}
          </div>

          {/* Portfolio (Optional) */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8">
            <h2 className="text-2xl font-semibold text-navy-800 mb-6">Portfolio (Optional)</h2>
            
            {/* Toggle */}
            <div className="flex gap-4 mb-6">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, portfolio_type: 'upload', portfolio_url: '' })}
                className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                  formData.portfolio_type === 'upload'
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-white/40 bg-white text-neutral-700 hover:border-slate-400'
                }`}
                data-testid="portfolio-upload-toggle"
              >
                <Upload className="w-5 h-5 inline mr-2" />
                Upload File
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, portfolio_type: 'url', portfolio_file_id: null, portfolio_file_name: '' })}
                className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                  formData.portfolio_type === 'url'
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-white/40 bg-white text-neutral-700 hover:border-slate-400'
                }`}
                data-testid="portfolio-url-toggle"
              >
                <LinkIcon className="w-5 h-5 inline mr-2" />
                Provide URL
              </button>
            </div>

            {formData.portfolio_type === 'upload' ? (
              !formData.portfolio_file_id ? (
                <div
                  {...portfolioDropzone.getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    portfolioDropzone.isDragActive
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-white/40 hover:border-teal-500'
                  }`}
                  data-testid="portfolio-dropzone"
                >
                  <input {...portfolioDropzone.getInputProps()} />
                  {uploadingFile === 'portfolio' ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-12 h-12 text-teal-500 animate-spin mb-4" />
                      <p className="text-neutral-600">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-12 h-12 text-neutral-400 mb-4" />
                      <p className="text-neutral-600 mb-2">
                        Drag & drop your portfolio, or click to browse
                      </p>
                      <p className="text-sm text-neutral-500">PDF, Images, ZIP (max 50MB)</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-emerald-600" />
                    <div>
                      <p className="font-medium text-navy-800">{formData.portfolio_file_name}</p>
                      <p className="text-sm text-emerald-600">Upload complete</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, portfolio_file_id: null, portfolio_file_name: '' })}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )
            ) : (
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Portfolio URL
                </label>
                <input
                  type="url"
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="Link to your portfolio"
                  data-testid="portfolio-url-input"
                />
              </div>
            )}
          </div>

          {/* Custom Fields */}
          {role?.custom_fields && role.custom_fields.length > 0 && (
            <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8">
              <h2 className="text-2xl font-semibold text-navy-800 mb-6">
                Additional Information
              </h2>
              <div className="space-y-6">
                {role.custom_fields.map((field, index) => (
                  <div key={index}>
                    <label className="block text-sm font-medium text-navy-700 mb-2">
                      {field.field_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      {field.required && ' *'}
                    </label>
                    {field.field_type === 'textarea' ? (
                      <textarea
                        required={field.required}
                        value={formData.custom_fields[field.field_name] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: {
                              ...formData.custom_fields,
                              [field.field_name]: e.target.value,
                            },
                          })
                        }
                        rows={4}
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                        data-testid={`custom-field-${field.field_name}`}
                      />
                    ) : field.field_type === 'number' ? (
                      <input
                        type="number"
                        required={field.required}
                        value={formData.custom_fields[field.field_name] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: {
                              ...formData.custom_fields,
                              [field.field_name]: e.target.value,
                            },
                          })
                        }
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        data-testid={`custom-field-${field.field_name}`}
                      />
                    ) : field.field_type === 'select' ? (
                      <select
                        required={field.required}
                        value={formData.custom_fields[field.field_name] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: {
                              ...formData.custom_fields,
                              [field.field_name]: e.target.value,
                            },
                          })
                        }
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        data-testid={`custom-field-${field.field_name}`}
                      >
                        <option value="">Select an option</option>
                        {field.options?.map((option, optIdx) => (
                          <option key={optIdx} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        required={field.required}
                        value={formData.custom_fields[field.field_name] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: {
                              ...formData.custom_fields,
                              [field.field_name]: e.target.value,
                            },
                          })
                        }
                        className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        data-testid={`custom-field-${field.field_name}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-6">
            <Link
              to="/roles"
              className="px-6 py-3 bg-white text-navy-700 border-2 border-white/40 rounded-xl hover:bg-white/60 hover:border-slate-400 transition-all duration-200 font-medium"
              data-testid="cancel-button"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 shadow-soft hover:shadow-soft font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              data-testid="submit-application-button"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2 w-5 h-5" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
